import { parseDelimited, checkRowCeiling, type Delimiter, type RowCeilingResult } from "@/lib/ingest";

/**
 * Pure summary of pasted spreadsheet text for the first-class Paste panel
 * (FAIL-473). Runs the same client-safe `parseDelimited` the import uses, then
 * derives the live feedback the panel shows BEFORE committing: delimiter,
 * row/column counts, a header + preview slice, and the free-tier row ceiling.
 * Header handling is caller-controlled (`hasHeader`) so the panel's toggle can
 * flip between "first row is headers" and synthesized `Column N` names.
 */
export interface PasteSummary {
  /** No non-empty rows yet — the panel shows its placeholder, CTA disabled. */
  empty: boolean;
  delimiter: Delimiter;
  columnCount: number;
  /** Data rows (excludes the header row when `hasHeader`). */
  dataRowCount: number;
  /** Column names: the first row when `hasHeader`, else `Column 1..N`. */
  header: string[];
  /** First `previewRows` data rows, for the preview grid. */
  preview: string[][];
  /** Free/hard row-ceiling verdict over the DATA rows. */
  ceiling: RowCeilingResult;
}

const DELIMITER_LABELS: Record<Delimiter, string> = {
  ",": "comma-separated",
  "\t": "tab-separated",
  ";": "semicolon-separated",
  "|": "pipe-separated",
};

/** Human label for a sniffed delimiter (falls back to the raw char). */
export function delimiterLabel(d: Delimiter): string {
  return DELIMITER_LABELS[d] ?? `“${d}”-separated`;
}

const rowIsBlank = (row: string[]): boolean => row.every((cell) => cell.trim() === "");

export function summarizePaste(
  text: string,
  opts: { hasHeader: boolean; previewRows?: number },
): PasteSummary {
  const previewRows = opts.previewRows ?? 5;
  const grid = parseDelimited(text);
  const nonEmpty = grid.rows.filter((r) => !rowIsBlank(r));

  if (nonEmpty.length === 0) {
    return { empty: true, delimiter: grid.delimiter ?? ",", columnCount: 0, dataRowCount: 0, header: [], preview: [], ceiling: { level: "ok" } };
  }

  const columnCount = Math.max(...nonEmpty.map((r) => r.length));
  const header = opts.hasHeader
    ? padRow(nonEmpty[0] ?? [], columnCount)
    : Array.from({ length: columnCount }, (_, i) => `Column ${i + 1}`);
  const dataRows = (opts.hasHeader ? nonEmpty.slice(1) : nonEmpty).map((r) => padRow(r, columnCount));

  return {
    empty: false,
    delimiter: grid.delimiter ?? ",",
    columnCount,
    dataRowCount: dataRows.length,
    header,
    preview: dataRows.slice(0, previewRows),
    ceiling: checkRowCeiling(dataRows.length),
  };
}

/** Pad a short row to the grid width so the preview grid stays rectangular. */
function padRow(row: string[], width: number): string[] {
  return row.length >= width ? row : [...row, ...Array.from({ length: width - row.length }, () => "")];
}
